<script lang="ts" setup>
import { computed, ref } from 'vue';
import { formatDateTimeStringToIso } from '@/utils/date-utils';
import {
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BSpinner,
  BTooltip,
} from 'bootstrap-vue-next';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { PAGING_ITEMS_PER_PAGE } from '@/constants';
import ComputeTaskList from '@/components/compute/ComputeTaskList.vue';

// --- Component references ---
const ComputeTaskListRef = ref<InstanceType<typeof ComputeTaskList>>();

// Filtering and paging state
const taskId = ref<number | null>(null);
const parentId = ref<number | null>(null);
const userId = ref<number | null>(null);
const userRole = ref<string | null>(null);
const projectId = ref<number | null>(null);
const bucketId = ref<number | null>(null);
const taskType = ref('');
const status = ref('');
const namePattern = ref('');

const dateCreatedAfter = ref<string | null>(null);
const dateCreatedBefore = ref<string | null>(null);
const timeCreatedAfter = ref<string | null>(null);
const timeCreatedBefore = ref<string | null>(null);
const dateTimeCreatedAfter = ref<string | null>(null);
const dateTimeCreatedBefore = ref<string | null>(null);

const dateUpdatedAfter = ref<string | null>(null);
const dateUpdatedBefore = ref<string | null>(null);
const timeUpdatedAfter = ref<string | null>(null);
const timeUpdatedBefore = ref<string | null>(null);
const dateTimeUpdatedAfter = ref<string | null>(null);
const dateTimeUpdatedBefore = ref<string | null>(null);

const dateCompletedAfter = ref<string | null>(null);
const dateCompletedBefore = ref<string | null>(null);
const timeCompletedAfter = ref<string | null>(null);
const timeCompletedBefore = ref<string | null>(null);
const dateTimeCompletedAfter = ref<string | null>(null);
const dateTimeCompletedBefore = ref<string | null>(null);

const dateAbortedAfter = ref<string | null>(null);
const dateAbortedBefore = ref<string | null>(null);
const timeAbortedAfter = ref<string | null>(null);
const timeAbortedBefore = ref<string | null>(null);
const dateTimeAbortedAfter = ref<string | null>(null);
const dateTimeAbortedBefore = ref<string | null>(null);

const pagingItemsPerPage = ref(PAGING_ITEMS_PER_PAGE);

const acrossUsers = computed(() => {
  return userId.value === null;
});

function clearFilter() {
  taskId.value = null;
  userId.value = null;
  projectId.value = null;
  bucketId.value = null;
  taskType.value = '';
  status.value = '';
  namePattern.value = '';

  dateCreatedBefore.value = null;
  timeCreatedBefore.value = null;
  dateTimeCreatedBefore.value = null;

  dateCreatedAfter.value = null;
  timeCreatedAfter.value = null;
  dateTimeCreatedAfter.value = null;

  dateUpdatedBefore.value = null;
  timeUpdatedBefore.value = null;
  dateTimeUpdatedBefore.value = null;

  dateUpdatedAfter.value = null;
  timeUpdatedAfter.value = null;
  dateTimeUpdatedAfter.value = null;

  dateCompletedBefore.value = null;
  timeCompletedBefore.value = null;
  dateTimeCompletedBefore.value = null;

  dateCompletedAfter.value = null;
  timeCompletedAfter.value = null;
  dateTimeCompletedAfter.value = null;

  dateAbortedBefore.value = null;
  timeAbortedBefore.value = null;
  dateTimeAbortedBefore.value = null;

  dateAbortedAfter.value = null;
  timeAbortedAfter.value = null;
  dateTimeAbortedAfter.value = null;

  requestSearch();
}

function requestSearch() {
  // check integer values are integer values
  if (!Number.isInteger(taskId.value)) {
    console.warn('Invalid task ID. Resetting to null.');
    taskId.value = null;
  }
  if (!Number.isInteger(parentId.value)) {
    console.warn('Invalid parent ID. Resetting to null.');
    parentId.value = null;
  }
  if (!Number.isInteger(userId.value)) {
    console.warn('Invalid user ID. Resetting to null.');
    userId.value = null;
  }
  if (!Number.isInteger(projectId.value)) {
    console.warn('Invalid project ID. Resetting to null.');
    projectId.value = null;
  }
  if (!Number.isInteger(bucketId.value)) {
    console.warn('Invalid bucket ID. Resetting to null.');
    bucketId.value = null;
  }

  // convert date and time to dateTime object (current timezone)
  dateTimeCreatedBefore.value = formatDateTimeStringToIso(
    dateCreatedBefore.value,
    timeCreatedBefore.value
  );
  dateTimeCreatedAfter.value = formatDateTimeStringToIso(
    dateCreatedAfter.value,
    timeCreatedAfter.value
  );
  dateTimeUpdatedBefore.value = formatDateTimeStringToIso(
    dateUpdatedBefore.value,
    timeUpdatedBefore.value
  );
  dateTimeUpdatedAfter.value = formatDateTimeStringToIso(
    dateUpdatedAfter.value,
    timeUpdatedAfter.value
  );
  dateTimeCompletedBefore.value = formatDateTimeStringToIso(
    dateCompletedBefore.value,
    timeCompletedBefore.value
  );
  dateTimeCompletedAfter.value = formatDateTimeStringToIso(
    dateCompletedAfter.value,
    timeCompletedAfter.value
  );
  dateTimeAbortedBefore.value = formatDateTimeStringToIso(
    dateAbortedBefore.value,
    timeAbortedBefore.value
  );
  dateTimeAbortedAfter.value = formatDateTimeStringToIso(
    dateAbortedAfter.value,
    timeAbortedAfter.value
  );

  // refetch the tasks with updated filters
  ComputeTaskListRef.value?.requestSearch();
}
</script>

<template>
  <!-- List compute tasks -->
  <h1 class="mb-4">
    <font-awesome-icon :icon="['fas', 'list-check']" /> Compute tasks
  </h1>
  <h5>Browse and manage compute tasks</h5>
  <hr />

  <!-- Content -->
  <div>
    <!-- Filter -->
    <div class="my-3">
      <BForm
        class="d-flex flex-row align-items-end flex-wrap gap-2"
        @submit.prevent="requestSearch"
      >
        <!-- Task ID -->
        <BFormGroup label="Task ID" label-for="task-id">
          <BFormInput
            id="task-id"
            type="number"
            v-model.number="taskId"
            placeholder="Task ID"
          />
        </BFormGroup>
        <!-- Parent ID -->
        <BFormGroup label="Parent Task ID" label-for="parent-id">
          <BFormInput
            id="parent-id"
            type="number"
            v-model.number="parentId"
            placeholder="Parent Task ID"
          />
        </BFormGroup>
        <!-- User ID and Role -->
        <BFormGroup label="User ID" label-for="user-id">
          <BFormInput
            id="user-id"
            type="number"
            v-model.number="userId"
            placeholder="User ID"
          />
        </BFormGroup>
        <!-- User ID and Role -->
        <BFormGroup label="User ID" label-for="user-id">
          <BFormInput
            id="user-id"
            type="number"
            v-model.number="userId"
            placeholder="User ID"
          />
        </BFormGroup>
        <BFormGroup>
          <label for="user-role">
            User role
            <font-awesome-icon
              id="user-role-tooltip-icon"
              icon="question-circle"
              class="ms-1 text-secondary"
            />
            <BTooltip target="user-role-tooltip-icon">
              Only applicable if user ID is specified.
            </BTooltip>
          </label>
          <BFormInput
            id="user-role"
            type="text"
            v-model="userRole"
            placeholder="e.g. owner, editor, viewer"
          />
        </BFormGroup>
        <!-- Project ID -->
        <BFormGroup label="Project ID" label-for="project-id">
          <BFormInput
            id="project-id"
            type="number"
            v-model.number="projectId"
            placeholder="Project ID"
          />
        </BFormGroup>
        <!-- Bucket ID -->
        <BFormGroup label="Bucket ID" label-for="bucket-id">
          <BFormInput
            id="bucket-id"
            type="number"
            v-model.number="bucketId"
            placeholder="Bucket ID"
          />
        </BFormGroup>
        <!-- Task type -->
        <BFormGroup label="Task type" label-for="search-action">
          <BFormInput
            id="task-type"
            type="text"
            v-model="taskType"
            placeholder="e.g. DATA_PIPELINE"
          />
        </BFormGroup>
        <!-- Task status -->
        <BFormGroup label="Status" label-for="task-status">
          <BFormInput
            id="task-status"
            type="text"
            v-model="status"
            placeholder="e.g. RUNNING"
          />
        </BFormGroup>
        <!-- Task name -->
        <BFormGroup label="Task name" label-for="task-name">
          <BFormInput
            id="search-text"
            type="text"
            v-model="namePattern"
            placeholder="e.g. CD% reconstruction"
          />
        </BFormGroup>
        <!-- Date created after -->
        <BFormGroup label="Date created after" label-for="date-created-after">
          <BFormInput
            id="date-created-after"
            type="date"
            v-model="dateCreatedAfter"
          />
        </BFormGroup>
        <BFormGroup label="Time created after" label-for="time-created-after">
          <BFormInput
            id="time-created-after"
            type="time"
            v-model="timeCreatedAfter"
          />
        </BFormGroup>
        <!-- Date created before -->
        <BFormGroup label="Date created before" label-for="date-created-before">
          <BFormInput
            id="date-created-before"
            type="date"
            v-model="dateCreatedBefore"
          />
        </BFormGroup>
        <BFormGroup label="Time created before" label-for="time-created-before">
          <BFormInput
            id="time-created-before"
            type="time"
            v-model="timeCreatedBefore"
          />
        </BFormGroup>
        <!-- Date updated after -->
        <BFormGroup label="Date updated after" label-for="date-updated-after">
          <BFormInput
            id="date-updated-after"
            type="date"
            v-model="dateUpdatedAfter"
          />
        </BFormGroup>
        <BFormGroup label="Time updated after" label-for="time-updated-after">
          <BFormInput
            id="time-updated-after"
            type="time"
            v-model="timeUpdatedAfter"
          />
        </BFormGroup>
        <!-- Date updated before -->
        <BFormGroup label="Date updated before" label-for="date-updated-before">
          <BFormInput
            id="date-updated-before"
            type="date"
            v-model="dateUpdatedBefore"
          />
        </BFormGroup>
        <BFormGroup label="Time updated before" label-for="time-updated-before">
          <BFormInput
            id="time-updated-before"
            type="time"
            v-model="timeUpdatedBefore"
          />
        </BFormGroup>
        <!-- Date completed after -->
        <BFormGroup
          label="Date completed after"
          label-for="date-completed-after"
        >
          <BFormInput
            id="date-completed-after"
            type="date"
            v-model="dateCompletedAfter"
          />
        </BFormGroup>
        <BFormGroup
          label="Time completed after"
          label-for="time-completed-after"
        >
          <BFormInput
            id="time-completed-after"
            type="time"
            v-model="timeCompletedAfter"
          />
        </BFormGroup>
        <!-- Date completed before -->
        <BFormGroup
          label="Date completed before"
          label-for="date-completed-before"
        >
          <BFormInput
            id="date-completed-before"
            type="date"
            v-model="dateCompletedBefore"
          />
        </BFormGroup>
        <BFormGroup
          label="Time completed before"
          label-for="time-completed-before"
        >
          <BFormInput
            id="time-completed-before"
            type="time"
            v-model="timeCompletedBefore"
          />
        </BFormGroup>
        <!-- Date aborted after -->
        <BFormGroup label="Date aborted after" label-for="date-aborted-after">
          <BFormInput
            id="date-aborted-after"
            type="date"
            v-model="dateAbortedAfter"
          />
        </BFormGroup>
        <BFormGroup label="Time aborted after" label-for="time-aborted-after">
          <BFormInput
            id="time-aborted-after"
            type="time"
            v-model="timeAbortedAfter"
          />
        </BFormGroup>
        <!-- Date aborted before -->
        <BFormGroup label="Date aborted before" label-for="date-aborted-before">
          <BFormInput
            id="date-aborted-before"
            type="date"
            v-model="dateAbortedBefore"
          />
        </BFormGroup>
        <BFormGroup label="Time aborted before" label-for="time-aborted-before">
          <BFormInput
            id="time-aborted-before"
            type="time"
            v-model="timeAbortedBefore"
          />
        </BFormGroup>
        <!-- Filter action buttons -->
        <BButton variant="outline-secondary" @click="clearFilter">
          <font-awesome-icon icon="trash" class="me-1" />
          Clear filter
        </BButton>
        <BButton
          variant="outline-dark"
          type="submit"
          :disabled="ComputeTaskListRef?.tasksQueryInProgress"
        >
          <span v-if="ComputeTaskListRef?.tasksQueryInProgress">
            <b-spinner small label=""></b-spinner>
            Searching
          </span>
          <span v-else>
            <font-awesome-icon icon="magnifying-glass" class="me-1" />
            Search
          </span>
        </BButton>
      </BForm>
    </div>

    <!-- LIST COMPUTE TASKS -->
    <ComputeTaskList
      ref="ComputeTaskListRef"
      parentName="Compute Tasks"
      :acrossUsers="acrossUsers"
      :taskId="taskId"
      :parentId="parentId"
      :userId="userId"
      :userRole="userRole"
      :projectId="projectId"
      :bucketId="bucketId"
      :taskType="taskType"
      :status="status"
      :namePattern="namePattern"
      :createdAfter="dateTimeCreatedAfter"
      :createdBefore="dateTimeCreatedBefore"
      :updatedAfter="dateTimeUpdatedAfter"
      :updatedBefore="dateTimeUpdatedBefore"
      :completedBefore="dateTimeCompletedBefore"
      :completedAfter="dateTimeCompletedAfter"
      :abortedAfter="dateTimeAbortedAfter"
      :abortedBefore="dateTimeAbortedBefore"
      :pagingItemsPerPage="pagingItemsPerPage"
    />
  </div>
</template>

<style scoped></style>
